import { NavigateFunction } from "react-router-dom";

export const ALPHA_PATHS = [
  "/alpha",
  "/alpha/dev-preview",
  "/alpha/recommendation",
  "/alpha/recommendation/dev-preview",
  "/alpha/brokerage",
  "/alpha/brokerage/dev-preview",
  "/alpha/ibkr",
  "/alpha/ibkr/confirmation",
  "/alpha/questionnaire",
  "/alpha/review",
  "/alpha/dashboard",
] as const;

export type AlphaPath = (typeof ALPHA_PATHS)[number];

export const ALPHA_INTERNAL_SECTION_STATE_KEY = "alphaInternalSection" as const;

const ALPHA_INTERNAL_SECTIONS = ["recommendation"] as const;

export type AlphaInternalSection = (typeof ALPHA_INTERNAL_SECTIONS)[number];

export type AlphaPreviousPathExpectation =
  | AlphaPath
  | readonly [AlphaPath, ...AlphaPath[]];

interface AlphaHistoryState {
  alphaPreviousPath?: unknown;
  [ALPHA_INTERNAL_SECTION_STATE_KEY]?: unknown;
}

const getPreviousAlphaPath = (state: unknown): AlphaPath | null => {
  if (!state || typeof state !== "object" || Array.isArray(state)) return null;

  const value = (state as AlphaHistoryState).alphaPreviousPath;
  return typeof value === "string"
    && ALPHA_PATHS.some((path) => path === value)
    ? value as AlphaPath
    : null;
};

export const getAlphaInternalSection = (
  state: unknown
): AlphaInternalSection | null => {
  if (!state || typeof state !== "object" || Array.isArray(state)) return null;

  const value = (state as AlphaHistoryState)[ALPHA_INTERNAL_SECTION_STATE_KEY];
  return typeof value === "string"
    && ALPHA_INTERNAL_SECTIONS.some((section) => section === value)
    ? value as AlphaInternalSection
    : null;
};

export const withAlphaInternalSectionState = <T extends Record<string, unknown>>(
  state: T,
  section: AlphaInternalSection
): T & Record<typeof ALPHA_INTERNAL_SECTION_STATE_KEY, AlphaInternalSection> => ({
  ...state,
  [ALPHA_INTERNAL_SECTION_STATE_KEY]: section,
});

const matchesExpectedPreviousPath = (
  previousPath: AlphaPath | null,
  expectedPreviousPath?: AlphaPreviousPathExpectation
) => previousPath !== null
  && expectedPreviousPath !== undefined
  && (typeof expectedPreviousPath === "string"
    ? previousPath === expectedPreviousPath
    : expectedPreviousPath.some((path) => path === previousPath));

export const replaceAlphaStepState = (
  navigate: NavigateFunction,
  currentPath: AlphaPath,
  state: Record<string, unknown>,
  currentLocationState: unknown,
  expectedPreviousPath?: AlphaPreviousPathExpectation
) => {
  const previousPath = getPreviousAlphaPath(currentLocationState);
  const currentEntryState = matchesExpectedPreviousPath(previousPath, expectedPreviousPath)
    ? { ...state, alphaPreviousPath: previousPath }
    : state;

  navigate(currentPath, { replace: true, state: currentEntryState });
};

export const advanceAlphaStep = (
  navigate: NavigateFunction,
  currentPath: AlphaPath,
  nextPath: AlphaPath,
  state: Record<string, unknown>,
  currentLocationState: unknown,
  expectedPreviousPath?: AlphaPreviousPathExpectation
) => {
  replaceAlphaStepState(
    navigate,
    currentPath,
    state,
    currentLocationState,
    expectedPreviousPath
  );
  navigate(nextPath, {
    state: {
      ...state,
      alphaPreviousPath: currentPath,
    },
  });
};

export const returnToPreviousAlphaStep = (
  navigate: NavigateFunction,
  currentLocationState: unknown,
  expectedPreviousPath: AlphaPreviousPathExpectation,
  fallbackState?: Record<string, unknown>,
  fallbackPath: AlphaPath = typeof expectedPreviousPath === "string"
    ? expectedPreviousPath
    : expectedPreviousPath[0]
) => {
  if (matchesExpectedPreviousPath(
    getPreviousAlphaPath(currentLocationState),
    expectedPreviousPath
  )) {
    navigate(-1);
    return;
  }

  navigate(fallbackPath, {
    replace: true,
    state: fallbackState && Object.keys(fallbackState).length > 0
      ? fallbackState
      : undefined,
  });
};

// Profile-update mode: the questionnaire entered outside the setup flow, for
// answering (or changing) the preference and risk questions without touching
// review, agreements, or brokerage. The route-state value is the page the
// questionnaire returns to on save or back.
export const ALPHA_PROFILE_UPDATE_RETURN_TO_STATE_KEY =
  "alphaProfileUpdateReturnTo" as const;

const ALPHA_PROFILE_UPDATE_RETURN_PATHS = [
  "/alpha/dashboard",
  // Returning here still passes the route's ADMIN/self-account guard.
  "/alpha/setupv2/dashboard",
  "/alpha/profile",
  "/alpha/analyze",
] as const;

export type AlphaProfileUpdateReturnPath =
  (typeof ALPHA_PROFILE_UPDATE_RETURN_PATHS)[number];

// A route state is untrusted input; anything but an allowlisted path means the
// questionnaire runs in normal setup mode.
export const getAlphaProfileUpdateReturnTo = (
  state: unknown
): AlphaProfileUpdateReturnPath | null => {
  if (!state || typeof state !== "object" || Array.isArray(state)) return null;

  const value = (state as Record<string, unknown>)[
    ALPHA_PROFILE_UPDATE_RETURN_TO_STATE_KEY
  ];
  return typeof value === "string"
    && ALPHA_PROFILE_UPDATE_RETURN_PATHS.some((path) => path === value)
    ? value as AlphaProfileUpdateReturnPath
    : null;
};

// Opens the risk questions in profile-update mode so the investor can answer or
// update their preference and risk answers without entering the setup flow (no
// review step, no agreement, no brokerage). The caller's location state is
// carried along so the questionnaire can hand it back on return. Shared so every
// entry point (dashboard preferences card, profile page, Why-Managed modal,
// allocations nudge) drives the exact same destination.
export const openAlphaRiskProfileUpdate = (
  navigate: NavigateFunction,
  currentLocationState: unknown,
  returnTo: AlphaProfileUpdateReturnPath
) => {
  const carriedState = currentLocationState
    && typeof currentLocationState === "object"
    && !Array.isArray(currentLocationState)
    ? currentLocationState as Record<string, unknown>
    : {};
  navigate("/alpha/questionnaire", {
    state: {
      ...carriedState,
      [ALPHA_PROFILE_UPDATE_RETURN_TO_STATE_KEY]: returnTo,
    },
  });
};

// Only Review may request an account-details recovery round trip.
export const ALPHA_ACCOUNT_DETAILS_RETURN_TO_STATE_KEY = "alphaAccountDetailsReturnTo";

export const getAlphaAccountDetailsReturnTo = (state: unknown): "/alpha/review" | null => {
    if (!state || typeof state !== "object" || Array.isArray(state)) {
        return null;
    }
    const returnPath = (state as Record<string, unknown>)[ALPHA_ACCOUNT_DETAILS_RETURN_TO_STATE_KEY];
    return returnPath === "/alpha/review" ? returnPath : null;
};
